// src/data/sports.js
// Sources:
//   - LISD MS Course Catalog 2026-27 (Athletics section, pp. 22-23)
//   - CRMS Athletics: https://sites.google.com/leanderisd.org/crms-athletics/home
//   - Cheerleading info from CRMS Clubs site
// Captured May 2026.

import { SOURCES } from './sources.js';

export default {
  // ============================================================
  // 6th-grade physical activity options (no UIL competition yet)
  // ============================================================
  sixthGrade: [
    {
      id: 'pre-athletics',
      name: 'Pre-Athletics',
      description:
        'Designed for 6th graders interested in 7th-grade UIL athletics. Meets PE requirements plus advanced curriculum and training tied to upcoming UIL sports (cross country, football, volleyball, basketball, soccer, track and field). Helps students decide whether to participate in athletics in 7th grade.',
      replacesPE: true,
      gender: 'separate',
      enrollment: 'Course selection — 7605 (B) / 7606 (G) year-long; 7609 (B) / 7610 (G) semester',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'lifetime-fitness-6',
      name: 'Lifetime Fitness',
      description:
        'PE course designed to teach the values of a healthy lifestyle. Exposure to team and individual sports plus health topics. Fitness assessments help students take ownership of their fitness levels. Goal-setting and sportsmanship.',
      replacesPE: true,
      gender: 'separate',
      enrollment: 'Course selection — 7601 (B) / 7602 (G) year-long; 7620 mixed',
      notes: 'Students supply their own workout clothes and shoes.',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'ocpe-6',
      name: 'Off-Campus PE (OCPE)',
      description:
        'Earn PE credit through approved off-campus programs. Category 1: Olympic-level (15+ hrs/week) — eligible for one hour daily school dismissal. Category 2: high-quality private/commercial programs (5+ hrs/week) — no school dismissal.',
      replacesPE: true,
      gender: 'coed',
      enrollment: 'Application required — deadline is the Friday after the last day of school',
      notes:
        'Fitnessgram required while enrolled. Full-year commitment. Available grades 6-8.',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'running-club-6',
      name: 'Running Club',
      description:
        'Open to all 6-8th graders. Improve fitness, prepare for Cross Country and Track & Field, or maintain current fitness. Each practice: dynamic warm-up, 20-30 minutes of running, cool down. Locker room time before first class. Bring water bottle, change of clothes, post-workout snack.',
      replacesPE: false,
      gender: 'coed',
      enrollment: 'Signed permission slip required',
      notes:
        'Practice begins 7:30 AM — early morning commitment. Coach Lichtenberger.',
      meetingTime: '7:30 AM',
      source: SOURCES.crmsClubs,
    },
  ],

  // ============================================================
  // UIL sports — available 7th and 8th grade only
  // ============================================================
  uilSports: [
    {
      id: 'football',
      name: 'Football',
      grades: [7, 8],
      gender: 'coed',
      season: 'Aug – Nov',
      seasonStart: 'August',
      seasonEnd: 'November',
      noCut: true,
      tryouts: 'No tryouts — sign up before season',
      practiceSchedule: 'Before/after school + during athletic period',
      gameSchedule: 'Tuesday afternoons',
      requirements: ['UIL physical (Rank One)', 'Athletic packet', 'No-pass-no-play eligibility'],
      enrollment:
        'Boys\' Athletics course (7721/7821) — choosing the course = signing up for football. Students who don\'t play football are placed in Lifetime Fitness instead.',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'volleyball',
      name: 'Volleyball',
      grades: [7, 8],
      gender: 'female',
      season: 'Sep – Oct',
      seasonStart: 'September',
      seasonEnd: 'October',
      noCut: false,
      tryouts: 'Tryouts the 1st week of school',
      practiceSchedule:
        '7th grade: before school + 1st period athletics. 8th grade: last period athletics + after school.',
      gameSchedule: 'Wednesday after school + Saturday tournaments',
      requirements: ['UIL physical (Rank One)', 'Athletic packet', 'No-pass-no-play eligibility'],
      enrollment: 'Girls\' Athletics course (7722/7822)',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'cross-country',
      name: 'Cross Country',
      grades: [7, 8],
      gender: 'coed-separate',
      season: 'Sep – Oct',
      seasonStart: 'September',
      seasonEnd: 'October',
      noCut: true,
      tryouts: 'No tryouts',
      practiceSchedule: 'Before and/or after school',
      gameSchedule: 'Mondays after school',
      requirements: ['UIL physical (Rank One)', 'Athletic packet', 'No-pass-no-play eligibility'],
      enrollment:
        'Athletic course not required to participate. Open to runners on gender-specific 7th and 8th grade teams.',
      notes: 'Races are 2 miles. Athletes typically run 2-6 miles per practice.',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'basketball',
      name: 'Basketball',
      grades: [7, 8],
      gender: 'coed-separate',
      season: 'Nov – Feb',
      seasonStart: 'November',
      seasonEnd: 'February',
      noCut: false,
      tryouts: 'Tryouts in November',
      practiceSchedule:
        '7th grade: before school + 1st period athletics. 8th grade: last period athletics + after school.',
      gameSchedule: 'Most games during the week. Tournaments on weekends.',
      requirements: ['UIL physical (Rank One)', 'Athletic packet', 'No-pass-no-play eligibility'],
      enrollment: 'Athletic course (Boys\' or Girls\' Athletics)',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'track-and-field',
      name: 'Track and Field',
      grades: [7, 8],
      gender: 'coed-separate',
      season: 'Feb – Apr',
      seasonStart: 'February',
      seasonEnd: 'April',
      noCut: true,
      tryouts: 'No tryouts',
      practiceSchedule: 'Before and/or after school',
      gameSchedule: 'Meets vary',
      requirements: ['UIL physical (Rank One)', 'Athletic packet', 'No-pass-no-play eligibility'],
      enrollment: 'Athletic course not required to participate',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'soccer',
      name: 'Soccer',
      grades: [7, 8],
      gender: 'coed-separate',
      season: 'Mar – May',
      seasonStart: 'March',
      seasonEnd: 'May',
      noCut: false,
      tryouts: 'Tryouts in March',
      practiceSchedule: 'Before and/or after school',
      gameSchedule: 'Varies',
      requirements: ['UIL physical (Rank One)', 'Athletic packet', 'No-pass-no-play eligibility'],
      enrollment:
        'Athletic course not required to participate. Teams are A and B, gender-specific, mixed 7th and 8th grade.',
      source: SOURCES.courseCatalog2627,
    },
  ],

  // ============================================================
  // Performance / spirit groups (open enrollment, year-long)
  // ============================================================
  performanceGroups: [
    {
      id: 'cheerleading',
      name: 'Cheerleading',
      grades: [7, 8],
      yearLong: true,
      tryoutsRequired: true,
      tryoutsTiming: 'Spring of 6th grade for 7th-grade season',
      tryoutSteps: 'Completion of evaluation steps + coach recommendation',
      coaches: 'Hannah Allred & Kate Martin',
      coachEmail: 'hannah.allred@leanderisd.org',
      performsAt: ['Football games', 'Volleyball games', 'Basketball games', 'Pep rallies'],
      practices: 'Daily before or after school',
      enrollment: 'Course 7711',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'color-guard',
      name: 'Color Guard',
      grades: [7, 8],
      yearLong: true,
      tryoutsRequired: false,
      tryoutsTiming: null,
      tryoutSteps: null,
      coaches: 'TBA',
      coachEmail: null,
      performsAt: ['Pep rallies', 'Competitions'],
      practices: 'Class period',
      enrollment: 'Course 7713 (grade 7) / 7813 (grade 8). Skills: flag, rifle, dance.',
      source: SOURCES.courseCatalog2627,
    },
    {
      id: 'dance-team',
      name: 'Dance Team',
      grades: [7, 8],
      yearLong: true,
      tryoutsRequired: true,
      tryoutsTiming: 'Spring clinics for following year',
      tryoutSteps: 'Clinic attendance + director recommendation',
      coaches: 'TBA',
      coachEmail: null,
      performsAt: ['Football games', 'Pep rallies', 'School functions'],
      practices: 'Daily before or after school',
      enrollment:
        'Course 6635 (Dance Team MS 1) for 7th grade; 6735 (Dance Team MS 2) for 8th. Open enrollment via clinic + recommendation. Dance MS 1 is NOT a prerequisite.',
      source: SOURCES.courseCatalog2627,
    },
  ],

  // ============================================================
  // Universal UIL eligibility requirements
  // ============================================================
  uilRequirements: {
    physical: {
      label: 'UIL Physical (Rank One)',
      description:
        'Annual physical exam required for all UIL sports participation. Submitted through the Rank One athletic platform.',
      annual: true,
      source: SOURCES.crmsAthletics,
    },
    noPassNoPlay: {
      label: 'No-Pass-No-Play',
      description:
        'Students must pass all classes each grading period to remain eligible for athletic participation.',
      source: SOURCES.crmsAthletics,
    },
    athleticPacket: {
      label: 'Athletic Packet',
      description:
        'Athletic paperwork must be completed and on file before tryouts or first practice.',
      source: SOURCES.crmsAthletics,
    },
    midSeasonTransfers: {
      label: 'Mid-Season Transfer Rule',
      description:
        'Students moving into the district mid-season cannot be placed in athletics without coach approval.',
      source: SOURCES.courseCatalog2627,
    },
  },
};
